import {
  BarChart3,
  Bell,
  Smartphone,
  CalendarCheck,
  Route,
  Banknote,
  ClipboardList,
  FileText,
  HandCoins,
  LayoutDashboard,
  Map,
  Navigation,
  Package,
  Factory,
  Settings,
  ShieldCheck,
  ShoppingCart,
  Store,
  Undo2,
  Warehouse,
  type LucideIcon,
} from 'lucide-react';
import { hasPermission } from '@/stores/auth.store';
import type { UserProfile } from '@/lib/api/types';

export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  group: 'main' | 'operations' | 'field' | 'admin';
  visible: (u: UserProfile) => boolean;
}

const can = (p: string) => (u: UserProfile) => hasPermission(u, p);
const any = (...ps: string[]) => (u: UserProfile) => ps.some((p) => hasPermission(u, p));
const supervisorOr = (...ps: string[]) => (u: UserProfile) => u.role === 'supervisor' || any(...ps)(u);

export const NAV_ITEMS: NavItem[] = [
  { to: '/', label: 'لوحة التحكم', icon: LayoutDashboard, group: 'main', visible: can('reports.view') },
  { to: '/invoices', label: 'الفواتير والموافقات', icon: FileText, group: 'main', visible: supervisorOr('sales.view_all', 'sales.approve') },
  { to: '/collections', label: 'التحصيل والديون', icon: HandCoins, group: 'main', visible: supervisorOr('collections.view_all') },
  { to: '/traders', label: 'التجار', icon: Store, group: 'main', visible: any('traders.manage', 'sales.view_all') },
  { to: '/wallets', label: 'المحافظ والتسليمات', icon: Banknote, group: 'main', visible: supervisorOr('wallets.view_all', 'wallets.receive_company') },
  { to: '/products', label: 'المنتجات والأسعار', icon: Package, group: 'operations', visible: can('products.manage') },
  { to: '/inventory', label: 'المخزون', icon: Warehouse, group: 'operations', visible: can('inventory.view') },
  { to: '/purchases', label: 'المشتريات', icon: ShoppingCart, group: 'operations', visible: can('purchases.manage') },
  { to: '/suppliers', label: 'الموردون', icon: Factory, group: 'operations', visible: can('purchases.manage') },
  { to: '/returns', label: 'المرتجعات', icon: Undo2, group: 'operations', visible: any('returns.approve', 'sales.view_all') },
  { to: '/app-orders', label: 'طلبات التطبيق', icon: Smartphone, group: 'operations', visible: can('app_orders.manage') },
  { to: '/regions', label: 'المناطق والخريطة', icon: Map, group: 'field', visible: any('regions.manage', 'reports.view') },
  { to: '/team', label: 'المندوبون والتتبع', icon: Navigation, group: 'field', visible: supervisorOr('tracking.view_all') },
  { to: '/visits', label: 'الزيارات والالتزام', icon: CalendarCheck, group: 'field', visible: supervisorOr('tracking.view_all') },
  { to: '/plans', label: 'جداول الزيارات', icon: Route, group: 'field', visible: supervisorOr('plans.manage', 'tracking.view_all') },
  { to: '/survey', label: 'الاستطلاع', icon: ClipboardList, group: 'field', visible: any('survey.view_all', 'survey.manage') },
  { to: '/reports', label: 'التقارير', icon: BarChart3, group: 'field', visible: supervisorOr('reports.view') },
  { to: '/users', label: 'المستخدمون والصلاحيات', icon: ShieldCheck, group: 'admin', visible: can('users.manage') },
  { to: '/notifications', label: 'الإشعارات', icon: Bell, group: 'admin', visible: can('notifications.send') },
  { to: '/settings', label: 'الإعدادات', icon: Settings, group: 'admin', visible: any('settings.manage', 'products.manage', 'audit.view') },
];

export const NAV_GROUPS: Record<NavItem['group'], string> = {
  main: 'القائمة الرئيسية',
  operations: 'البضاعة',
  field: 'الميدان',
  admin: 'الإدارة',
};

/** First page the user may open (field users without dashboard access land elsewhere). */
export const homeFor = (u: UserProfile) => NAV_ITEMS.find((i) => i.visible(u))?.to ?? '/no-access';
